import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

// Count up to a target number once the element scrolls into view.
const useCountUp = (target, duration = 1600) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
          setValue(Math.round(eased * target));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);

  return [value, ref];
};

const Stat = ({ target, suffix = "+", caption, testid }) => {
  const [value, ref] = useCountUp(target);
  return (
    <div data-testid={testid} ref={ref}>
      <p className="font-sans font-semibold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white leading-none tabular-nums">
        {value.toLocaleString("en-GB")}{suffix}
      </p>
      <div
        className="h-px my-5"
        style={{ background: "linear-gradient(90deg, #d9b45f 0%, #e6c877 35%, rgba(207,166,74,0.15) 100%)" }}
      />
      <p className="text-sm sm:text-base text-white/60">{caption}</p>
    </div>
  );
};

export const StatsBanner = () => (
  <section className="bg-[#141210] py-16 sm:py-20 lg:py-24" data-testid="stats-banner">
    <div className="max-w-[1800px] mx-auto px-5 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr_1fr] gap-y-12 gap-x-10 lg:gap-x-16 items-start">
        <div>
          <h2 className="font-sans font-semibold tracking-tight text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.12] text-white max-w-md">
            Because the perfect bridal look shouldn't{" "}
            <span className="text-pink">cost you a fortune in fine jewellery.</span>
          </h2>
          <Link
            to="/shop/all"
            data-testid="stats-shop-all"
            className="inline-flex items-center gap-2 mt-7 text-sm text-white/80 hover:text-white border-b border-white/40 hover:border-white pb-1 transition-colors"
          >
            Shop All <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <Stat target={500} caption="Have already chosen Zebaa." testid="stat-customers" />
        <Stat target={5} caption="5-star reviews on Green Street." testid="stat-reviews" />
      </div>
    </div>
  </section>
);
